import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  useGetBlobSASTokenMutation,
  useGetUserAnnotationsQuery,
  usePostAnnotationMutation,
} from "features/api/apiSlice";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Alert,
  Button,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Snackbar,
} from "@mui/material";
import TimeSeriesChart, {
  TimeSeriesAnnotation,
  TimeSeriesValue,
} from "components/charts/TimeSeriesChart";
import "components/charts/TimeSeriesChart.css";
import { PlusOutlined } from "@ant-design/icons";
import React from "react";
import AddAnnotationDialog from "components/dialogs/Annotations/AddAnnotationDialog";
import { useTranslation } from "react-i18next";
import { IUser, UserProviderId } from "models/IUser";
import { DeviceBrandName } from "models/ISensor";
import { useGetSensorTypesQuery } from "features/api/apiSlice";
import { IPreprocessedFileInfo } from "models/IPreprocessedFileInfo";
import MainCard from "components/MainCard";
import i18n from "app/i18n";

type RangeDate = {
  from: Date;
  to: Date;
};

interface Props {
  patientId: string;
  users: IUser[];
  preprocessedFilesInfo: IPreprocessedFileInfo[];
}

export function SessionChart({ patientId, users, preprocessedFilesInfo }: Props) {
  const { t } = useTranslation();
  const user = users!!.find((x) => x.id == patientId)!!;

  const [rangeDates, setRangeDates] = useState<RangeDate>({
    from: moment().startOf("day").subtract(1, "days").toDate(),
    to: moment().endOf("day").toDate(),
  });
  const [searchParams] = useSearchParams();
  const [timeSeriesValues, setTimeSeriesValues] = useState<TimeSeriesValue[]>([]);
  const [timeSeriesAnnotations, setTimeSeriesAnnotations] = useState<TimeSeriesAnnotation[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const annotationQueryRange = useMemo(() => {
    if (!timeSeriesValues.length) {
      return null;
    }

    const minDate = timeSeriesValues[0].date;
    const maxDate = timeSeriesValues[timeSeriesValues.length - 1].date;

    return {
      from: minDate.toISOString(),
      to: maxDate.toISOString(),
    };
  }, [timeSeriesValues]);

  const { data: sensorTypes = [] } = useGetSensorTypesQuery();
  const { data: annotations } = useGetUserAnnotationsQuery(
    user.id && annotationQueryRange
      ? {
          userId: user.id,
          from: annotationQueryRange.from,
          to: annotationQueryRange.to,
        }
      : skipToken
  );

  useEffect(() => {
    const fileQuery = searchParams.get("file");
    if (fileQuery) {
      setSelectedFile(fileQuery);
    }

    const fromQuery = searchParams.get("from");
    const toQuery = searchParams.get("to");
    setRangeDates({
      from:
        fromQuery && toQuery
          ? moment(fromQuery, "YYYY-MM-DDTHH:mm:ssZ").toDate()
          : moment().startOf("day").subtract(1, "days").toDate(),
      to:
        fromQuery && toQuery
          ? moment(toQuery, "YYYY-MM-DDTHH:mm:ssZ").toDate()
          : moment().endOf("day").toDate(),
    });
  }, [user.id, searchParams]);

  const [getBlobSASToken] = useGetBlobSASTokenMutation();
  useEffect(() => {
    async function loadCsv() {
      if (!selectedFile) {
        setTimeSeriesValues([]);
        return;
      }
      setIsLoading(true);

      try {
        //Get SAS Url
        const getBlobSASTokenResponse = await getBlobSASToken({ containerName: DeviceBrandName.Sykno.toLowerCase(), getBlobSasToken: { BlobName: selectedFile } }).unwrap();
        const response = await fetch(getBlobSASTokenResponse.data.url);
        if (!response.ok) throw new Error(`Failed to fetch CSV: ${response.statusText}`);
        const csvText = await response.text();

        // Parse CSV
        const rows = csvText.split("\n").filter(r => r.trim().length > 0);
        if (rows.length < 5) throw new Error("CSV does not contain enough lines.");

        // Read metadata
        let startTime = moment();
        let format: string[] = [];
        let sampleRate = 0;

        for (let i = 0; i < 4; i++) {
          const line = rows[i].trim();
          if (line.startsWith("# Start time:")) {
            startTime = moment(line.replace("# Start time:", "").trim());
          } else if (line.startsWith("# Format:")) {
            format = line.replace("# Format:", "").trim().split(",");
          } else if (line.startsWith("# Sample rate:")) {
            sampleRate = parseFloat(line.replace("# Sample rate:", "").trim().split(" ")[0]);
          }
        }

        // Parse data from line 5
        const intervalMs = 1000 / sampleRate;
        const data: TimeSeriesValue[] = [];

        for (let i = 4; i < rows.length; i++) {
          const line = rows[i].trim();
          if (!line) continue;
          const values = line.split(",");

          const timestamp = startTime.clone().add((i - 4) * intervalMs, "ms").toDate();

          const sensorsMap: { [csvColumn: string]: string } = {
            "distance (mm)": 'Distance',
            "distance_breath (mm)": 'Distance breath',
            "distance_pulse (mm)": 'Distance pulse',
            "distance_hsound (mm)": 'Distance heart sound',
            "BR (bpm)": 'BR',
            "HR (bpm)": 'HR',
          };

          Object.entries(sensorsMap).forEach(([csvColumn, sensorType]) => {
            const index = format.indexOf(csvColumn);
            if (index !== -1) {
              const value = parseFloat(values[index]);
              if (!isNaN(value)) {
                data.push({
                  id: selectedFile,
                  name: sensorType,
                  date: timestamp,
                  value,
                  units: "",
                });
              }
            }
          });
        }

        setTimeSeriesValues(data);
      } catch (error) {
        console.error("Error loading CSV:", error);
        setTimeSeriesValues([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadCsv();
  }, [selectedFile, rangeDates, sensorTypes]);

  useEffect(() => {
    if (annotations && annotations.filter((c) => c.timelineDate).length > 0 && users) {
      const data = annotations.map((c) => {
        return {
          userRole: users!!.find((x) => x.id == c.creationUserId)?.role,
          date: new Date(c.timelineDate),
          description: c.text,
        } as TimeSeriesAnnotation;
      });
      setTimeSeriesAnnotations(data);
    }
  }, [annotations, users]);

  const [showAddAnnotationDialog, setShowAddAnnotationDialog] = useState<boolean>(false);
  const handleOpenAddAnnotationDialog = () => setShowAddAnnotationDialog(true);
  const handleCloseAddAnnotationDialog = () => {
    setTimelineDate(null);
    setShowAddAnnotationDialog(false);
  };

  const [postAnnotation] = usePostAnnotationMutation();
  const handleAddAnnotation = async (timelineDate: string, text: string) => {
    await postAnnotation({ userId: user.id!!, timelineDate, text })
      .unwrap()
      .then(() => setShowAddAnnotationDialog(false))
      .catch((error) => {
        setSnackbarMessage(
          t(error.data.literalKey as string, error.data.message as string)
        );
        setShowSnackbar(true);
      });
  };

  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>();
  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowSnackbar(false);
    setSnackbarMessage(null);
  };

  const [timelineDate, setTimelineDate] = useState<Date | null>(null);
  const handleOpenAddAnnotationDialogWithTimelineDate = (timelineDate: Date) => {
    setTimelineDate(timelineDate);
    setShowAddAnnotationDialog(true);
  };

  return (
    <>
      <MainCard>
        <Grid container sx={{ width: "100%" }} spacing={{ xs: 0, md: 2 }} paddingTop={3}>
          <Grid container item wrap="nowrap" spacing={2}>
            <Grid item container wrap="nowrap" xs={true}>
              <FormControl sx={{ minWidth: "200px", marginRight: "15px" }}>
                <InputLabel id="preprocessed-files-label" shrink={preprocessedFilesInfo.length > 0}>
                  {t("sessions", "Sessions")}
                </InputLabel>
                <Select
                  id="preprocessed-files"
                  value={selectedFile ?? ""}
                  onChange={(event: SelectChangeEvent<string>) => {
                    setSelectedFile(event.target.value);
                  }}
                  input={<OutlinedInput id="select-preprocessed-files" />}
                  size="small"
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                    borderColor: "grey.400",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.light",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    height: "41.13px",
                  }}
                >
                  {preprocessedFilesInfo.map((preprocessedFileInfo) => {
                    return (
                      <MenuItem key={preprocessedFileInfo.blobName} value={preprocessedFileInfo.blobName}>
                        <ListItemText primary={moment(preprocessedFileInfo.creationDate).locale(i18n.language).format('l LTS')} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {user.provider !== UserProviderId.SMILE && user.provider !== UserProviderId.VETLLAR && user.provider !== UserProviderId.AURA && (
                <Grid item container alignItems="center" justifyContent="flex-end" xs="auto">
                  <Button
                    variant="contained"
                    startIcon={<PlusOutlined />}
                    onClick={handleOpenAddAnnotationDialog}
                    sx={{ textTransform: "none" }}
                  >
                    {t("add_annotation", "Add annotation")}
                  </Button>
                </Grid>
              )}
          </Grid>

          <Grid item xs={12}>
            <TimeSeriesChart
              showLoading={isLoading}
              values={timeSeriesValues}
              annotations={timeSeriesAnnotations}
              enableAuraSplitAxes={true}
              handleOpenAddAnnotationDialogWithTimelineDate={
                handleOpenAddAnnotationDialogWithTimelineDate
              }
            />
          </Grid>
        </Grid>

        {showAddAnnotationDialog && (
          <AddAnnotationDialog
            openDialog={showAddAnnotationDialog}
            handleClose={handleCloseAddAnnotationDialog}
            handleAddAnnotation={handleAddAnnotation}
            timelineDate={timelineDate}
          />
        )}

        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </MainCard>
    </>
  );
}
